import Overlay from 'ol/Overlay';
import {createStringXY} from 'ol/coordinate';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import {circular} from 'ol/geom/Polygon';
import {fromLonLat, toLonLat} from 'ol/proj';
import Control from 'ol/control/Control';
import ScaleLine from 'ol/control/ScaleLine';

export function setupPopup(map) {
  const container = document.getElementById('popup');
  if (!container) return null;
  const content = document.getElementById('popup-content');
  const closer = document.getElementById('popup-closer');

  const overlay = new Overlay({
    element: container,
    autoPan: true,
    autoPanAnimation: { duration: 250 },
  });
  map.addOverlay(overlay);

  if (closer) {
    closer.onclick = function () {
      overlay.setPosition(undefined);
      closer.blur();
      return false;
    };
  }

  return { overlay, content };
}

export function setupMousePosition(map) {
  var nav = document.getElementById('app-nav');
  if (!nav) return null;

  var el = document.createElement('div');
  el.id = 'mouse-position';

  var search = nav.querySelector('.nav-search');
  if (search) {
    nav.insertBefore(el, search);
  } else {
    nav.appendChild(el);
  }

  var format = createStringXY(4);
  map.on('pointermove', function (evt) {
    if (evt.dragging) return;
    var coord = toLonLat(evt.coordinate);
    el.textContent = format(coord);
  });

  return el;
}

export function setupContourHover(map, contoursLayer, overlay) {
  var lastTime = 0;
  var hideTimer = null;
  map.on('pointermove', function (evt) {
    var now = Date.now();
    if (now - lastTime < 50) return;
    lastTime = now;

    var feature = map.forEachFeatureAtPixel(evt.pixel, function (f) {
      return f;
    }, { layerFilter: function (l) { return l === contoursLayer; } });

    if (!feature) return;

    var elev = feature.getProperties()['elevation'];
    if (elev != null) {
      var info = document.getElementById('mouse-position');
      if (info) {
        info.innerHTML = '<pre>Elevation: ' + elev + 'm</pre>';
      }
      var content = document.getElementById('popup-content');
      if (content) {
        content.innerHTML = '<b>Elevation:</b> ' + elev + 'm';
      }
      if (overlay) {
        overlay.setPosition(evt.coordinate);
        if (hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(function () {
          overlay.setPosition(undefined);
        }, 2000);
      }
    }
  });
}

export function setupContourSlider(contoursUpdateFn, initialInterval) {
  var el = document.getElementById('slider-id');
  // slider-id may be the input itself or a wrapper div containing it
  var sliderEl = (el && el.tagName === 'INPUT') ? el : (el ? el.querySelector('input[type="range"]') : null);
  const sliderOutput = document.getElementById('slider-output');
  if (!sliderEl) return initialInterval;

  var interval = initialInterval || 100;
  sliderEl.addEventListener('input', function () {
    interval = Number(this.value);
    if (sliderOutput) sliderOutput.textContent = interval;
    var info = document.getElementById('mouse-position');
    if (info) info.innerHTML = '<pre>Contour interval: ' + interval + 'm</pre>';
    contoursUpdateFn(interval);
  });
  return interval;
}

/**
 * Wire up layer visibility toggles from setupNavLayers checkboxes.
 * layerCbs: object of checkbox elements keyed by id (from setupNavLayers).
 * layerMap: object mapping the same ids to OL layer instances.
 */
export function setupLayerVisibility(layerCbs, layerMap) {
  Object.keys(layerCbs).forEach(function (id) {
    var layer = layerMap[id];
    if (!layer) return;
    layer.setVisible(layerCbs[id].checked);
    layerCbs[id].addEventListener('change', function () {
      if (this.type !== 'radio') {
        layer.setVisible(this.checked);
        return;
      }
      // A radio only fires on the one being checked, so sync its whole group.
      var group = this.name;
      Object.keys(layerCbs).forEach(function (other) {
        if (layerCbs[other].name === group && layerMap[other]) {
          layerMap[other].setVisible(layerCbs[other].checked);
        }
      });
    });
  });
}

/**
 * Set up common map controls: popup, mouse position, contour hover, scale line, geolocation, left control grouping.
 * Returns {popup, geo}.
 */
export function setupCommonControls(map, contoursLayer) {
  var popup = setupPopup(map);
  setupMousePosition(map);
  setupContourHover(map, contoursLayer, popup ? popup.overlay : null);
  // How long 100 m (or 1 km...) is on the map right now.
  map.addControl(new ScaleLine({units: 'metric', minWidth: 80}));
  var geo = setupGeolocation(map);
  map.addLayer(geo.layer);
  groupLeftControls(map);
  return {popup, geo};
}

/**
 * Create a slider row DOM element for nav dropdowns.
 */
export function makeSliderRow(labelText, id, min, max, step, value, opts) {
  var prefix = (opts && opts.prefix) || '';
  var suffix = (opts && opts.suffix) || '';
  var row = document.createElement('div');
  row.className = 'ctrl-row';
  var lbl = document.createElement('label');
  lbl.textContent = labelText;
  var input = document.createElement('input');
  input.type = 'range';
  input.id = id;
  input.min = min;
  input.max = max;
  input.step = step || '1';
  input.value = value;
  var span = document.createElement('span');
  span.innerHTML = prefix + '<span id="' + id + 'Out">' + value + '</span>' + suffix;
  row.appendChild(lbl);
  row.appendChild(input);
  row.appendChild(span);
  return row;
}

export function setupGeolocation(map) {
  const source = new VectorSource();
  const layer = new VectorLayer({ source: source });

  var lastUpdate = 0;
  navigator.geolocation.watchPosition(
    function (pos) {
      var now = Date.now();
      if (now - lastUpdate < 2000) return;
      lastUpdate = now;
      const coords = [pos.coords.longitude, pos.coords.latitude];
      const accuracy = circular(coords, pos.coords.accuracy);
      source.clear(true);
      source.addFeatures([
        new Feature(accuracy.transform('EPSG:4326', map.getView().getProjection())),
        new Feature(new Point(fromLonLat(coords))),
      ]);
    },
    function (error) {
      console.warn('Geolocation error:', error.message);
    },
    { enableHighAccuracy: true }
  );

  const locate = document.createElement('div');
  locate.className = 'ol-control ol-unselectable locate';
  locate.innerHTML = '<button title="Locate me">◎</button>';
  locate.addEventListener('click', function () {
    if (!source.isEmpty()) {
      map.getView().fit(source.getExtent(), { maxZoom: 15, duration: 500 });
    }
  });
  map.addControl(new Control({ element: locate }));

  return { source, layer };
}

/**
 * Group left-side OL controls (zoom, locate) into a flex container
 * so they stack with uniform spacing.
 */
export function groupLeftControls(map) {
  var mapEl = map.getTargetElement();
  if (!mapEl) return;

  var container = document.createElement('div');
  container.className = 'left-controls';

  // Find and move controls into the container
  setTimeout(function () {
    var zoom = mapEl.querySelector('.ol-zoom');
    var locate = mapEl.querySelector('.locate');

    if (zoom) container.appendChild(zoom);
    if (locate) container.appendChild(locate);

    var viewport = mapEl.querySelector('.ol-overlaycontainer-stopevent');
    if (viewport) {
      viewport.appendChild(container);
    } else {
      mapEl.appendChild(container);
    }
  }, 0);
}
